import { Check, X } from 'lucide-react'
import { cn } from './internal/utils'

const Step = ({ children }) => children

const STATUS_STYLE = {
  finish: {
    dot: 'border-[var(--primary-color)] bg-[var(--primary-color)] text-white',
    title: 'text-[var(--primary-color)]',
  },
  process: {
    dot: 'border-[var(--primary-color)] bg-[var(--primary-color)] text-white',
    title: 'text-[var(--text-dark)] font-semibold',
  },
  wait: {
    dot: 'border-[var(--border-strong)] bg-[var(--surface-card)] text-[var(--gray-600)]',
    title: 'text-[var(--text-muted)]',
  },
  error: {
    dot: 'border-[var(--error-color)] bg-[var(--error-color)] text-white',
    title: 'text-[var(--error-color)]',
  },
}

const resolveStatus = (item, index, current, overallStatus) => {
  if (item.status) return item.status
  if (index < current) return 'finish'
  if (index === current) return overallStatus === 'error' ? 'error' : 'process'
  return 'wait'
}

/**
 * antd Steps. `items`-driven, horizontal or vertical, with `onChange` making the
 * steps clickable. index.css pins `.ui-steps-item-container` metrics, so those
 * class hooks are preserved.
 */
const Steps = ({
  items = [],
  current = 0,
  status = 'process',
  direction = 'horizontal',
  size = 'default',
  labelPlacement,
  onChange,
  className,
  style,
  ...rest
}) => {
  const isVertical = direction === 'vertical'
  const isClickable = typeof onChange === 'function'

  return (
    <ol
      className={cn(
        'ui-steps flex list-none p-0',
        isVertical ? 'ui-steps-vertical flex-col gap-4' : 'ui-steps-horizontal items-start gap-2 overflow-auto',
        size === 'small' && 'ui-steps-small text-sm',
        className
      )}
      style={style}
      {...rest}
    >
      {items.map((item, index) => {
        const itemStatus = resolveStatus(item, index, current, status)
        const skin = STATUS_STYLE[itemStatus] || STATUS_STYLE.wait
        const isLast = index === items.length - 1

        const marker = (
          <span
            className={cn(
              'ui-steps-item-icon flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
              skin.dot
            )}
          >
            {item.icon ??
              (itemStatus === 'finish' ? (
                <Check size={14} aria-hidden="true" />
              ) : itemStatus === 'error' ? (
                <X size={14} aria-hidden="true" />
              ) : (
                index + 1
              ))}
          </span>
        )

        const label = (
          <span className="min-w-0">
            <span className={cn('ui-steps-item-title block text-sm', skin.title)}>{item.title}</span>
            {item.subTitle ? (
              <span className="ui-steps-item-subtitle block text-xs text-[var(--text-muted)]">
                {item.subTitle}
              </span>
            ) : null}
            {item.description ? (
              <span className="ui-steps-item-description block text-xs text-[var(--text-muted)]">
                {item.description}
              </span>
            ) : null}
          </span>
        )

        const body = (
          <span
            className={cn(
              'ui-steps-item-container flex min-w-[180px] items-center gap-2 text-left',
              labelPlacement === 'vertical' && 'flex-col items-center text-center'
            )}
          >
            {marker}
            {label}
          </span>
        )

        return (
          <li
            key={item.key ?? index}
            aria-current={index === current ? 'step' : undefined}
            className={cn(
              'ui-steps-item flex min-w-0 items-center gap-2',
              `ui-steps-item-${itemStatus}`,
              !isVertical && 'flex-1'
            )}
          >
            {isClickable && item.disabled !== true ? (
              <button
                type="button"
                onClick={() => onChange(index)}
                className="min-h-[var(--control-height)] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)]"
              >
                {body}
              </button>
            ) : (
              body
            )}
            {!isLast && !isVertical ? (
              <span className="ui-steps-item-tail h-px min-w-6 flex-1 bg-[var(--border-color)]" />
            ) : null}
          </li>
        )
      })}
    </ol>
  )
}

Steps.Step = Step

export default Steps
